import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, Loader2, Store } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated && user) {
            const redirectPath = user.role === 'atendente' ? '/admin/pdv' : '/admin/dashboard';
            navigate(redirectPath, { replace: true });
        }
    }, [isAuthenticated, user, navigate]);

    if (isAuthenticated && user) {
        const redirectPath = user.role === 'atendente' ? '/admin/pdv' : '/admin/dashboard';
        return <Navigate to={redirectPath} replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const result = await login(email, password);
        
        if (result.success) {
            toast.success('Login realizado com sucesso!');
            setTimeout(() => {
                const token = localStorage.getItem('token');
                if (token) {
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        const redirectPath = payload.role === 'atendente' ? '/admin/pdv' : '/admin/dashboard';
                        window.location.href = redirectPath;
                    } catch {
                        window.location.href = '/admin/dashboard';
                    }
                }
            }, 100);
        } else {
            toast.error(result.error);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black flex flex-col">
            {/* Dark Header with Neon Red accent */}
            <div className="bg-gradient-to-r from-black via-gray-900 to-black py-8 border-b border-red-500/30">
                <div className="flex items-center justify-center gap-3">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-600 to-red-500 flex items-center justify-center shadow-lg shadow-red-500/40">
                        <Store className="w-9 h-9 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Empório FN</h1>
                        <p className="text-red-400 text-sm font-medium">Mercearia & Minimercado</p>
                    </div>
                </div>
            </div>
            
            {/* Login Form */}
            <div className="flex-1 flex items-center justify-center p-4 -mt-8">
                <Card className="w-full max-w-md bg-gray-900 rounded-2xl shadow-xl border border-red-500/20">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="text-xl font-bold text-white">
                            Área Administrativa
                        </CardTitle>
                        <p className="text-gray-400 text-sm">Faça login para continuar</p>
                    </CardHeader>
                    
                    <CardContent className="pt-4">
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-300 font-medium">
                                    Email
                                </Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="seu@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-11 h-12 bg-gray-800 border-gray-700 text-white rounded-xl focus:border-red-500 focus:ring-red-500 placeholder:text-gray-500"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-gray-300 font-medium">
                                    Senha
                                </Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-11 h-12 bg-gray-800 border-gray-700 text-white rounded-xl focus:border-red-500 focus:ring-red-500 placeholder:text-gray-500"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <Button
                                type="submit"
                                className="w-full h-12 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-bold rounded-xl shadow-lg shadow-red-500/30 transition-all duration-300"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Entrando...
                                    </>
                                ) : (
                                    'Entrar'
                                )}
                            </Button>
                        </form>
                        
                        <div className="mt-6 pt-6 border-t border-gray-800 text-center">
                            <p className="text-xs text-gray-500">
                                Sistema de Gestão Empório FN
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default LoginPage;
